import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Euro, ChevronRight, Sparkles, TrendingDown, Wallet } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import AiAnalysisCard from "@/components/AiAnalysisCard";

const COST_ITEMS = [
  { id: "boarding", nl: "Stalling (pension)", en: "Boarding (livery)", default: 550, hint_nl: "Volpension €450-€750 · halfpension €250-€400", hint_en: "Full board €450-€750 · part board €250-€400" },
  { id: "farrier", nl: "Hoefsmid (per 6-8 wkn)", en: "Farrier (every 6-8 wks)", default: 75, hint_nl: "Bekappen €40-€60 · beslaan €80-€140", hint_en: "Trim €40-€60 · shoeing €80-€140", multiplier: 6 / 6.5 },
  { id: "vet", nl: "Dierenarts (gemiddeld/mnd)", en: "Vet (avg/month)", default: 50, hint_nl: "Routine + vaccins ~€600/jaar = €50/mnd", hint_en: "Routine + vaccines ~€600/year = €50/mo" },
  { id: "insurance", nl: "Verzekering (WA + dood)", en: "Insurance (liability + mortality)", default: 60, hint_nl: "Afhankelijk van waarde paard", hint_en: "Depends on horse value" },
  { id: "feed", nl: "Krachtvoer/supplementen extra", en: "Concentrate/supplements extra", default: 60, hint_nl: "Boven de stalling-prijs", hint_en: "On top of boarding price" },
  { id: "lessons", nl: "Lessen (1x/wk)", en: "Lessons (1x/wk)", default: 160, hint_nl: "€35-€50 per les × 4", hint_en: "€35-€50 per lesson × 4" },
  { id: "competitions", nl: "Wedstrijden + transport", en: "Competitions + transport", default: 80, hint_nl: "Inschrijvingen + brandstof", hint_en: "Entry fees + fuel" },
  { id: "equipment", nl: "Materiaal (slijtage gemiddeld)", en: "Equipment (avg depreciation)", default: 40, hint_nl: "Zadeldek, hoofdstel-onderdelen, etc.", hint_en: "Saddle pads, bridle parts, etc." },
  { id: "dental", nl: "Gebit (2x/jaar)", en: "Dentist (2x/year)", default: 20, hint_nl: "~€120 × 2 = €240/jaar = €20/mnd", hint_en: "~€120 × 2 = €240/yr = €20/mo" },
  { id: "other", nl: "Overig (parasitair, fysio, etc.)", en: "Other (parasitology, physio, etc.)", default: 35, hint_nl: "Wormkuren + occasioneel fysio", hint_en: "Dewormer + occasional physio" },
];

const HorseCostCalculatorPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [costs, setCosts] = useState(() => Object.fromEntries(COST_ITEMS.map(i => [i.id, i.default])));

  useEffect(() => {
    document.title = isNl
      ? "Paardenkosten Calculator — hoeveel kost een paard per maand? | HorseAppCompare"
      : "Horse Cost Calculator — how much does a horse cost per month? | HorseAppCompare";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", isNl
      ? "Bereken de werkelijke maandelijkse kosten van een paard: stalling, dierenarts, hoefsmid, lessen, verzekering en meer. Inclusief jaartotaal en bespaar-tips."
      : "Calculate the real monthly cost of owning a horse: boarding, vet, farrier, lessons, insurance and more. Includes annual total and money-saving tips.");
  }, [isNl]);

  const totals = useMemo(() => {
    const monthly = COST_ITEMS.reduce((sum, i) => sum + (parseFloat(costs[i.id]) || 0), 0);
    return { monthly: Math.round(monthly), yearly: Math.round(monthly * 12) };
  }, [costs]);

  const sortedTop = useMemo(() => [...COST_ITEMS]
    .map(i => ({ ...i, amt: parseFloat(costs[i.id]) || 0 }))
    .sort((a, b) => b.amt - a.amt).slice(0, 3), [costs]);

  return (
    <main data-testid="cost-page">
      <section className="bg-gradient-to-br from-emerald-50 via-white to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-14 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4"><Link to="/" className="hover:text-[#2563EB]">Home</Link><ChevronRight size={12} /><span className="text-[#0F172A] font-medium">{isNl ? "Maandkosten" : "Monthly cost"}</span></nav>
          <div className="overline mb-2 text-emerald-700 inline-flex items-center gap-1.5"><Euro size={12} /> {isNl ? "Gratis tool" : "Free tool"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-3">{isNl ? "Paardenkosten Calculator" : "Horse Cost Calculator"}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">{isNl
            ? "Hoeveel kost een paard per maand? Pas de bedragen aan jouw situatie aan — stalling, dierenarts, hoefsmid, lessen, verzekering — en zie het totaalplaatje."
            : "How much does a horse cost per month? Adjust each line to your situation — boarding, vet, farrier, lessons, insurance — and see the full picture."}</p>
        </div>
      </section>

      <section className="hac-container py-10 max-w-6xl grid lg:grid-cols-[1fr_400px] gap-6">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
          <h2 className="font-bold text-xl text-[#0F172A] mb-2">{isNl ? "Pas elke kostenpost aan" : "Adjust each cost"}</h2>
          {COST_ITEMS.map((i) => (
            <div key={i.id} className="flex items-center gap-3 border-b border-[#F1F5F9] pb-3 last:border-0">
              <div className="flex-1">
                <div className="text-sm font-medium text-[#0F172A]">{isNl ? i.nl : i.en}</div>
                <div className="text-[11px] text-[#94A3B8]">{isNl ? i.hint_nl : i.hint_en}</div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[#64748B]">€</span>
                <input type="number" min="0" max="9999" className="w-24 text-right hac-input !py-1.5" value={costs[i.id]} onChange={(e) => setCosts({ ...costs, [i.id]: e.target.value })} data-testid={`cost-${i.id}`} />
                <span className="text-[11px] text-[#94A3B8]">/{isNl ? "mnd" : "mo"}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-emerald-200 mb-2 inline-flex items-center gap-1.5"><Wallet size={12} /> {isNl ? "Totale kosten" : "Total cost"}</div>
            <div className="text-5xl font-bold mb-1 tabular-nums" data-testid="cost-monthly">€{totals.monthly}<span className="text-lg text-emerald-200">/{isNl ? "mnd" : "mo"}</span></div>
            <div className="text-emerald-200 text-sm">≈ €{totals.yearly.toLocaleString(isNl ? "nl-NL" : "en-GB")} / {isNl ? "jaar" : "year"}</div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <h3 className="font-bold text-[#0F172A] mb-3 inline-flex items-center gap-2"><Sparkles size={16} className="text-emerald-600" /> {isNl ? "Top 3 kostenposten" : "Top 3 cost drivers"}</h3>
            <ol className="space-y-2 text-sm">
              {sortedTop.map((i, idx) => (
                <li key={i.id} className="flex items-center justify-between">
                  <span className="text-[#0F172A]"><span className="font-mono text-emerald-600 mr-2">{idx + 1}.</span>{isNl ? i.nl : i.en}</span>
                  <span className="font-bold tabular-nums">€{Math.round(i.amt)}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs">
            <p className="text-amber-900 inline-flex items-start gap-2"><TrendingDown size={14} className="text-amber-600 shrink-0 mt-0.5" /><span>{isNl ? "Waar mensen vaak besparen: halfpension i.p.v. volpension, zelf voer halen, jaarvaccins combineren, en korting bij hoefsmid via stalafspraken." : "Where owners save: part board vs full board, sourcing feed yourself, combining annual vaccines, and farrier discounts via barn deals."}</span></p>
          </div>

          <Link to="/apps?category=stable" className="block bg-[#0F172A] hover:bg-[#1E293B] text-white text-center rounded-xl p-4 text-sm transition-colors">{isNl ? "Bekijk boekhoud-apps voor paarden →" : "See horse accounting apps →"}</Link>

          <AiAnalysisCard tool="cost" accent="emerald" inputs={{
            monthly: totals.monthly,
            yearly: totals.yearly,
            top_costs: sortedTop.map(t => `${isNl ? t.nl : t.en} €${Math.round(t.amt)}`).join(", "),
            breakdown: COST_ITEMS.map(i => `${isNl ? i.nl : i.en}: €${parseFloat(costs[i.id]) || 0}`).join("; "),
          }} />
        </div>
      </section>
    </main>
  );
};

export default HorseCostCalculatorPage;
